import { assign, createMachine, fromPromise } from 'xstate';
import { isEmail } from '../utils/typeGuards';
import { getApp } from 'firebase/app';
import {
  getAuth,
  verifyPasswordResetCode,
  confirmPasswordReset,
  sendPasswordResetEmail,
} from 'firebase/auth';

const app = getApp();
const auth = getAuth(app);

const verifyCode = fromPromise(
  async ({ input }: { input: { resetCode: string } }) => {
    const response = verifyPasswordResetCode(auth, `${input.resetCode}`);
    return response;
  },
);
const setNewPassword = fromPromise(
  async ({ input }: { input: { resetCode: string; password: string } }) => {
    const response = confirmPasswordReset(
      auth,
      `${input.resetCode}`,
      input.password,
    );
    return response;
  },
);
const requestResetLink = fromPromise(
  async ({ input }: { input: { email: string } }) => {
    const response = sendPasswordResetEmail(auth, input.email, {
      url: 'https://mimo.org/web/passwordReset/',
    });
    return response;
  },
);

export type PasswordResetStateEvent =
  | {
      type: 'ENTER_EMAIL';
      email?: string;
    }
  | {
      type: 'ENTER_PASSWORD';
      password?: string;
    }
  | {
      type: 'REQUEST_RESET';
    }
  | {
      type: 'SUBMIT_PASSWORD';
    }
  | {
      type: 'VERIFY';
      resetCode: string;
    }
  | {
      type: 'INITIALISE_FOR_REQUESTING_EMAIL';
    }
  | {
      type: 'INITIALISE_FOR_CHANGING_THE_PASSWORD';
    }
  | {
      type: 'RESTART';
    };

const passwordResetStateMachine = createMachine(
  {
    /** @xstate-layout N4IgpgJg5mDOIC5QAcCGtYHcD2AnCASnGAC4DKJqJYAdAJYB2dJAxAJIBybAKmwIIAZNmQCiAfQBiAeQJiCIgIoBVEWV4cA4mJEBZPmwEBtAAwBdRCmyxmdbAwsgAHogBMxmsYCMATgAcvzwA2AFZPX0DvYwAWFwAaEABPRABaFwBmXxoXXxjg32iXTxcAdgBfUvi0DBx8IlhSCipaRmZ2Ll5BYXFpWQBhAAk+TU4tbn7xAAU+MjIAdRkAERNzJBBkKxs7B2cEIOLPGm8omMDfQsLg73ikhFTg929Hl0DAwuM04wDg8sr0LDxCMRyJRqDRcGAAI4AVzgJAmfxqgPqJBEAFtUHQADb0JgkOioTEsEQcbgiWS6fRGMwOdbWPFbVY7IKZQL7bx7QIxFzeYLXRBFFwuGgvYr3bz7HK+NLeH5rBEAuoNEG0cHQ2Hw6oKoFojHYlp4gkseTKVTcOSqETcZY0jb0+yMlJpY40UVeYyBYxeaVRVl8hDeNLBYWi4LFKKeKJ+T4uWVVf61IGNUGqmGwOHyhPInVYnE2Q3GlRqc2iK2eFaWOm2e2gHZRXzFGj1wUuPIennFK6JRCRQI0XI5NJhTwZKJlCpyzWZpVNMGQ1PpydI0jZ7Ep2GMKCKlHorEsCB2ZoMABu2AA1rQ44it0mVXP1Rml9vdbO1WmN1uVwhGCeAMZUKvLNaqy0ps1ZOIg7gRtyjyeMEPrBIEPj3IEfoRgcUQZJ8xjBKEbzBGksYPteyovvOGrxo+K6keuDCbtqO6EmAuC4HgNDIJiVAAGZ4KibFEYmJFrmm5FXvRz5CXitEfgxX7Htgf52oB1LAbaVbbCk3hCj49bRN4nJ5OKvJdggLh1i6xj7KGcGCv4-iEYuxEzj+AAWqC0WAIkArmBqEgAamSbASAAmkBFagepuzhg2Xg5JpORuGcnY3COjbFBkHphh8Hq+N846XlqyI3jQLluTAnn4DQR5MXQnEJFuvTYBAYB7geOInuefEOQJTmue55UQJV1W1fVjVgLJv7-nYSnlmsqkMjW-LFMUmTPDl7wBHpFlpH6kbeI2gROqG6SmRZMp5fxhUkSVfUPoNuA1XVQINU1LBMSxuBsRxJDcbgvH5VOwI9aVHm3VV93DU9o3jfJk0MNNNqVvN4G7EtK2nPcGQ+Bl23GWkxRaekOWimEhS+PZFGOaC11lbd4KoBACQSHg-VsAwyBQqwxKkrIUwzPMBBLMpYV2hFbh5DQrauqKbJRDtxh7f4nJ+OE8ERGOvxdZdQM3Yus704zzMPqz7OsGQSgAEI6DwYi83MiyhbNiNgTsgqclkoZLRGficn6YqHOylz7O8asxudmvTlTvU07rdMM0zuAs2zHMsGblvW7b-NLGWCPhQ6Jl1kKGThOK+wHchfppE6fYbRZOWIWk6S5RrFPdZHwP9bOyLPWAIiOMgdDghARqmnwBBWkLju5wtuynO4rISnjxyDhZfrpFEEsNwGzx4067zk6JWttzrFGdS3yKsxMLFQOCGAtQwh7tReF0R7Q1Mg7r-2PhfV837A0MKQBMwDsQIizztyCyNBBxwR8PBJ0TpfaeAbMcVkitIynSiPvAqL9ipR3fifT+W5v7YGvnAWAr1mKsXYlxHip8D7YLfh3AhQIiEkIwP-WG8MVJO1FsgyBxRWTgJQRGYofp9gNhCMtPSUpshOjOuOBgo14DAWfoDagOdQHT2SMcdwrpPDuk9MOI4vpjLJFCL2Iog4cppBeB8JamCAZFX1OotSedPCwQONhPGntsghB5H6LR2RhTHXdAdNwlc4L2MfEVCS-VpK6mcUjHYyRpRCl0for0RiRHGXwocTCoSELYQDIESJlNbyvgXGfZcDFvL4kxAk52-J2SpSlGlU6B1K5xGMkURBkD8jLVMppUIYQSmtzKWRFRVEJLvjEliepEVknOjSR6DJPosk3GHEtDw2Q14vHdOGMmYdKmqLGfecOT4cxMKzAxMgYAGAkDmXnfwc8xHckDPWPION1nZA8dsp0uzOTDMOXQ45OD24PgedPIoxQHjMmWgEP5dZUKFAltkLa1ieT1gDCMw+r9cEd31LUiFyNhxhEOLC-ww4fSIuMiUSCLTBRmJyG47F9C8WgyGo9Luo0iVMlDHtaxDKPgBG5EZZKORGz3H2T4axYRilAqwSChhtMwD63jonE2PLXCel7NvKU7p2R1gjL7I4hxpEthysONwodm7AqKkqmOkMmq937oPTVJk3SHCWl60MHpsir3lls-IPZORpXZCyxVbKP4qJYb-N1zxXiNnSFjFpeyULZNgocdIgYijhjQmdG1Cq7WRvwSoiQLRYDOUgG6wcpkPAxC8B2e4hNRWIHwntGIlc6yRAFQRcopQgA */
    id: 'passwordResetState',
    initial: 'init',
    context: {
      errors: {
        email: '',
        password: '',
        serviceError: '',
      },
      resetCode: '',
      email: '',
      password: '',
    },
    types: {} as {
      events: PasswordResetStateEvent;
      actors:
        | {
            src: 'verifyCode';
            id: 'verifyCode';
            logic: typeof verifyCode;
          }
        | {
            src: 'setNewPassword';
            id: 'setNewPassword';
            logic: typeof setNewPassword;
          }
        | {
            src: 'requestResetLink';
            id: 'requestResetLink';
            logic: typeof requestResetLink;
          };
    },
    states: {
      init: {
        on: {
          INITIALISE_FOR_REQUESTING_EMAIL: {
            target: 'requestPasswordResetEmail',
          },
          INITIALISE_FOR_CHANGING_THE_PASSWORD: {
            target: 'changePassword',
          },
        },
      },
      requestPasswordResetEmail: {
        initial: 'initial',
        states: {
          initial: {
            on: {
              ENTER_EMAIL: {
                actions: 'captureEmail',
              },
              REQUEST_RESET: [
                {
                  guard: 'isEmailInvalid',
                  target: 'initial',
                  actions: 'validateFields',
                },
                {
                  target: 'requestingResetEmail',
                },
              ],
            },
          },
          requestingResetEmail: {
            invoke: {
              src: 'requestResetLink',
              id: 'requestResetLink',
              input: ({ context }) => ({
                email: context.email,
              }),
              onDone: {
                target: 'passwordResetEmailSent',
                actions: 'clearServiceError',
              },
              onError: [
                {
                  actions: 'captureServiceError',
                  target: 'initial',
                },
              ],
            },
          },
          passwordResetEmailSent: {
            type: 'final',
          },
        },
      },
      changePassword: {
        initial: 'initial',
        states: {
          initial: {
            on: {
              VERIFY: {
                target: 'verifyResetCode',
                actions: 'setResetCode',
              },
              ENTER_EMAIL: {
                actions: 'captureEmail',
              },
            },
          },
          verifyResetCode: {
            invoke: {
              src: 'verifyCode',
              id: 'verifyCode',
              input: ({ context }) => ({
                resetCode: context.resetCode,
              }),
              onDone: {
                target: 'readyForPasswordInput',
                actions: 'clearServiceError',
              },
              onError: {
                target: 'resetCodeExpired',
              },
            },
          },
          readyForPasswordInput: {
            on: {
              ENTER_PASSWORD: {
                actions: 'capturePassword',
              },
              SUBMIT_PASSWORD: [
                {
                  guard: 'isPasswordInvalid',
                  target: 'readyForPasswordInput',
                  actions: 'validateFields',
                },
                {
                  target: 'passwordResetInProgress',
                },
              ],
            },
          },
          resetCodeExpired: {
            on: {
              RESTART: {
                target: '#passwordResetState.requestPasswordResetEmail',
              }, // GO TO requestPasswordResetEmail
            },
          },
          passwordResetInProgress: {
            invoke: {
              src: 'setNewPassword',
              id: 'setNewPassword',
              input: ({ context }) => ({
                resetCode: context.resetCode,
                password: context.password,
              }),
              onDone: {
                target: 'passwordResetFinished',
                actions: 'clearServiceError',
              },
              onError: [
                {
                  actions: 'captureServiceError',
                  target: 'readyForPasswordInput',
                },
              ],
            },
          },
          passwordResetFinished: {},
        },
      },
    },
  },
  {
    guards: {
      isEmailInvalid: ({ context }) => !isEmail(context.email),
      isPasswordInvalid: ({ context }) => context.password.length < 6,
    },
    actions: {
      captureEmail: assign(({ context, event }) => {
        if (event.type === 'ENTER_EMAIL') {
          return {
            ...context,
            email: event.email,
            errors: {
              ...context.errors,
              email: '',
            },
          };
        }
        return context;
      }),
      capturePassword: assign(({ context, event }) => {
        if (event.type === 'ENTER_PASSWORD') {
          return {
            ...context,
            password: event.password,
            errors: {
              ...context.errors,
              password: '',
            },
          };
        }
        return context;
      }),
      validateFields: assign(({ context, event }) => {
        if (event.type === 'REQUEST_RESET') {
          if (!isEmail(context.email)) {
            return {
              ...context,
              errors: {
                ...context.errors,
                email: 'Invalid email address format',
              },
            };
          }
        }

        if (event.type === 'SUBMIT_PASSWORD') {
          if (context.password.length < 7) {
            return {
              ...context,
              errors: {
                ...context.errors,
                password: 'The password must be at least 6 characters long',
              },
            };
          }
        }
        return context;
      }),
      captureServiceError: assign(({ context, event }) => {
        return {
          ...context,
          errors: {
            ...context.errors,
            serviceError: (event as any).error.message,
          },
        };
      }),
      setResetCode: assign(({ context, event }) => {
        if (event.type === 'VERIFY') {
          return {
            ...context,
            resetCode: event.resetCode,
          };
        }
        return context;
      }),
      clearServiceError: assign(({ context, event }) => {
        return {
          ...context,
          errors: {
            ...context.errors,
            serviceError: '',
          },
        };
      }),
    },
    actors: {
      requestResetLink: requestResetLink,
      verifyCode: verifyCode,
      setNewPassword: setNewPassword,
    },
  },
);

export default passwordResetStateMachine;
